import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ExternalLink, Info } from 'lucide-react';

import droneProcessingService, { type DroneProcessResponse } from '../services/droneProcessingService.ts';
import { useAppState } from '../contexts/AppStateContext.tsx';

interface DroneProcessingPanelProps {
  onClose: () => void;
}

const exampleUrls = [
  'https://signed-url-1',
  'https://signed-url-2',
  'https://signed-url-3',
].join('\n');

const DroneProcessingPanel: React.FC<DroneProcessingPanelProps> = ({ onClose: _onClose }) => {
  const [projectId, setProjectId] = useState('drone-project-001');
  const [outputPrefixOverride, setOutputPrefixOverride] = useState('');
  const [imageUrlsText, setImageUrlsText] = useState('');
  const [apiAvailable, setApiAvailable] = useState(false);
  const [isCheckingApi, setIsCheckingApi] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<DroneProcessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { addNotification } = useAppState();

  useEffect(() => {
    const checkAPI = async () => {
      setIsCheckingApi(true);
      const healthy = await droneProcessingService.checkAPIHealth();
      setApiAvailable(healthy);
      setIsCheckingApi(false);
    };

    checkAPI();
  }, []);

  const imageUrls = useMemo(
    () => imageUrlsText.split(/\r?\n/).map(line => line.trim()).filter(Boolean),
    [imageUrlsText]
  );

  const notifyError = (message: string) => {
    addNotification({ kind: 'drone-processing', severity: 'error', title: 'Drone Agent', message });
  };

  const handleSubmit = async () => {
    if (!projectId.trim()) {
      const message = 'Project ID is required';
      setError(message);
      notifyError(message);
      return;
    }

    if (imageUrls.length < 2) {
      const message = 'Provide at least two signed JPEG URLs from the same flight set';
      setError(message);
      notifyError(message);
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const response = await droneProcessingService.submitDroneJob({
        projectId: projectId.trim(),
        imageUrls,
        outputPrefixOverride: outputPrefixOverride.trim() || undefined,
      });

      setResult(response);
      addNotification({
        kind: 'drone-processing',
        severity: 'success',
        title: 'Drone Agent',
        message: `Processing completed for ${response.project_id}`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Drone processing failed';
      setError(message);
      notifyError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="w-full space-y-5 text-gray-100">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-2xl font-bold text-amber-300">Drone Agent</h2>
            <span className="inline-flex items-center rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-200">
              Internal Beta
            </span>
          </div>
          <p className="text-sm text-gray-400 max-w-2xl">
            Submit temporary import URLs to the Rust Drone Agent. It will stage imagery in secure processing storage, run NodeODM, and prepare orthomosaic and mesh assets for export back to connected user storage.
          </p>
        </div>
      </div>

      <div className={`rounded-lg border px-4 py-3 text-sm ${apiAvailable ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200' : 'border-yellow-500/40 bg-yellow-500/10 text-yellow-200'}`}>
        {isCheckingApi ? 'Checking Drone Agent readiness...' : apiAvailable ? 'Drone Agent Cloud Run service is reachable.' : 'Drone Agent service is not reachable right now. You can still review inputs, but submission will likely fail until the service is healthy.'}
      </div>

      <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 p-4 text-sm text-sky-100">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="space-y-1">
            <p className="font-semibold">Input contract</p>
            <p>Use temporary signed JPEG URLs from the same overlapping flight set. In this beta, local files are first staged into secure processing storage before photogrammetry begins.</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="space-y-1">
            <p className="font-semibold">Storage model</p>
            <p>LandSurv is moving toward connected destination storage such as Google Drive, Dropbox, or customer-owned object storage. The current beta still returns output links from temporary processing storage.</p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-gray-300">Project ID</span>
          <input
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            disabled={isProcessing}
            className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-amber-400 focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-gray-300">Processing Prefix Override</span>
          <input
            value={outputPrefixOverride}
            onChange={(e) => setOutputPrefixOverride(e.target.value)}
            disabled={isProcessing}
            placeholder="optional/staging/prefix"
            className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-amber-400 focus:outline-none"
          />
        </label>
      </div>

      <label className="block">
        <span className="mb-2 block text-sm font-medium text-gray-300">Signed JPEG URLs</span>
        <textarea
          value={imageUrlsText}
          onChange={(e) => setImageUrlsText(e.target.value)}
          disabled={isProcessing}
          rows={8}
          placeholder={exampleUrls}
          className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-amber-400 focus:outline-none"
        />
        <span className="mt-2 block text-xs text-gray-500">One URL per line. Current count: {imageUrls.length}</span>
      </label>

      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>{error}</div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <div className="text-xs text-gray-500">
          The current beta call is synchronous and may take several minutes while files sit in temporary processing storage.
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isProcessing || isCheckingApi}
          className="rounded-lg border border-amber-300/50 bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600 px-4 py-2 text-sm font-semibold text-gray-950 transition hover:from-amber-300 hover:via-amber-400 hover:to-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isProcessing ? 'Processing...' : 'Run Drone Pipeline'}
        </button>
      </div>

      {result && (
        <div className="space-y-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5">
          <div>
            <h3 className="text-lg font-semibold text-emerald-200">Processing complete</h3>
            <p className="mt-1 text-sm text-emerald-100">Project <span className="font-mono">{result.project_id}</span> finished with task <span className="font-mono">{result.task_id}</span>. These links currently point to processing-stage outputs.</p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <AssetLink label="Orthophoto" href={result.orthophoto_uri} />
            <AssetLink label="Mesh OBJ" href={result.mesh_obj_uri} />
            {result.mesh_mtl_uri ? <AssetLink label="Mesh MTL" href={result.mesh_mtl_uri} /> : null}
            <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Textures</div>
              <div className="space-y-1 text-sm">
                {result.texture_uris.length > 0 ? result.texture_uris.map((uri) => (
                  <a key={uri} href={uri} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200 break-all">
                    <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>{uri}</span>
                  </a>
                )) : <span className="text-gray-500">No textures returned</span>}
              </div>
            </div>
          </div>

          {result.warnings.length > 0 && (
            <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-100">
              <div className="mb-1 font-semibold">Warnings</div>
              <ul className="list-disc pl-5">
                {result.warnings.map((warning) => <li key={warning}>{warning}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AssetLink: React.FC<{ label: string; href: string }> = ({ label, href }) => (
  <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-3">
    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</div>
    <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-sm text-cyan-300 hover:text-cyan-200 break-all">
      <ExternalLink className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
      <span>{href}</span>
    </a>
  </div>
);

export default DroneProcessingPanel;